import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Globe from '../components/Globe';
import CountryInfo from '../components/CountryInfo';
import Navbar from '../components/Navbar';
import { AchievementToastContainer } from '../components/AchievementToast';
import { fetchCountries } from '../api/countryApi';
import { sendCountryViewed } from '../api/launcherApi';

const styles = {
  container: {
    width: '100%',
    minHeight: '100vh',
    position: 'relative',
  },
  loading: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    zIndex: 1000,
  },
  loadingSpinner: {
    width: '50px',
    height: '50px',
    border: '3px solid rgba(255, 255, 255, 0.1)',
    borderTop: '3px solid #00d4ff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '16px',
  },
  error: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    color: '#ff6b6b',
    padding: '20px',
    background: 'rgba(0, 0, 0, 0.8)',
    borderRadius: '10px',
    zIndex: 1000,
  },
  retryButton: {
    marginTop: '15px',
    padding: '10px 20px',
    background: '#00d4ff',
    border: 'none',
    borderRadius: '5px',
    color: '#000',
    cursor: 'pointer',
    fontSize: '14px',
  },
};

const HomePage = () => {
  const { t } = useTranslation();
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingAchievements, setPendingAchievements] = useState([]);

  const clearAchievements = useCallback(() => {
    setPendingAchievements([]);
  }, []);

  const loadCountries = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchCountries();
      // Filter out countries without valid coordinates
      const validCountries = data.filter(
        (c) => c.latlng && c.latlng.length === 2 && c.latlng[0] !== 0 && c.latlng[1] !== 0
      );
      setCountries(validCountries);
    } catch (err) {
      setError(t('home.loadError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCountries();
  }, []);

  const filteredCountries = useMemo(() => {
    if (!searchTerm) return countries;
    const term = searchTerm.toLowerCase();
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.capital?.toLowerCase().includes(term) ||
        c.region?.toLowerCase().includes(term)
    );
  }, [countries, searchTerm]);

  // Track viewed countries to avoid duplicate events
  const viewedCountries = useRef(new Set());

  const handleCountryClick = async (country) => {
    setSelectedCountry(country);

    // Send event to launcher (only once per country per session)
    if (country && !viewedCountries.current.has(country.code)) {
      viewedCountries.current.add(country.code);
      const result = await sendCountryViewed({
        country_code: country.code,
        country_name: country.name,
        region: country.region,
      });

      // Show achievement toast if any
      if (result?.new_achievements?.length > 0) {
        setPendingAchievements(result.new_achievements);
      }
    }
  };

  const handleCloseInfo = () => {
    setSelectedCountry(null);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        <div style={styles.loading}>
          <div style={styles.loadingSpinner}></div>
          <p style={styles.loadingText}>{t('home.loadingCountries')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <p>{error}</p>
          <button style={styles.retryButton} onClick={loadCountries}>
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <AchievementToastContainer
        achievements={pendingAchievements}
        onClear={clearAchievements}
      />
      <Navbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        countryCount={filteredCountries.length}
      />
      <Globe
        countries={filteredCountries}
        onCountryClick={handleCountryClick}
        selectedCountry={selectedCountry}
      />
      <CountryInfo country={selectedCountry} onClose={handleCloseInfo} />
    </div>
  );
};

export default HomePage;
